import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText
} from "@solomon-labs/design/components/ui";
import React from "react";

import {
    DISABLED_VALUE_CLASS,
    LIGHTBAR_FIELD_MEASURE
} from "@/pages/shared/lightbarField";

/**
 * The optional free-text label of a saved destination: the same label-inside
 * light-bar field as `AddressField` (DS `InputGroup` › inline-start
 * `InputGroupAddon`, no divider › `InputGroupInput`), minus the numeric face
 * and trim, since a label is free text. The value keeps the DS Input default
 * type (`small`), like every field in these flows. Shared by the
 * add-destination form, the Add a Position rewards step and the Rewards
 * Destinations table's in-place rename (`prefix={false}`: no inline label,
 * the placeholder names the field, and it fits its cell).
 */
export function LabelField({
    value,
    onChange,
    onBlur,
    onKeyDown,
    disabled = false,
    className,
    prefix = true,
    inputRef,
    placeholder = "e.g. Operations Wallet",
    describedBy,
    size = "md",
    prefixLabel = "Label (Optional)",
    fill = false,
    prefixClassName
}: {
    value: string;
    onChange: (next: string) => void;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    className?: string;
    /** Show "Label (Optional)" inside the field (default). Without it the
     *  field fills its container instead of the form measure. */
    prefix?: boolean;
    inputRef?: (element: HTMLInputElement | null) => void;
    placeholder?: string;
    /** The id of a message about the field (e.g. a save error). */
    describedBy?: string;
    /** `sm`: the DS Input `sm` shell (32px, same type), for a table cell. */
    size?: "sm" | "md";
    /** The inline label (and accessible name). */
    prefixLabel?: string;
    /** Fill the container (as the Add a Position fields do) instead of the
     *  form measure. */
    fill?: boolean;
    /** Classes for the inline label, e.g. a shared width so stacked fields'
     *  values start on one line. */
    prefixClassName?: string;
}): React.ReactElement {
    return (
        <InputGroup
            aria-label={prefixLabel}
            className={`${prefix && !fill ? LIGHTBAR_FIELD_MEASURE : "w-full"} ${className ?? ""}`}
        >
            {prefix && (
                <InputGroupAddon align="inline-start">
                    <InputGroupText className={prefixClassName}>
                        {prefixLabel}
                    </InputGroupText>
                </InputGroupAddon>
            )}
            <InputGroupInput
                ref={inputRef}
                aria-label={prefixLabel}
                aria-describedby={describedBy}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                disabled={disabled}
                placeholder={placeholder}
                className={`${size === "sm" ? "h-8" : ""} ${DISABLED_VALUE_CLASS}`}
            />
        </InputGroup>
    );
}
